import { useToken } from '@chakra-ui/react';
import { mergeWith } from '@chakra-ui/utils';
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from 'react';
import {
  useColorToken,
  useColorValue,
  useMobile,
  useOpposingColor,
  useOpposingColorCallback,
} from '~/hooks';
import { useSelectContext } from './select';

import * as ReactSelect from 'react-select';
import type { SingleOption } from '~/types';
import type { RSStyleCallbackProps, RSStyleFunction, RSThemeFunction } from './types';

export const useContainerStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'container', Opt, IsMulti> => {
  return useCallback((base, state) => {
    return { width: '100%' };
  }, []);
};

export const useControlStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'control', Opt, IsMulti> => {
  const { colorMode } = props;
  const { isError } = useSelectContext();

  const minHeight = '56px';
  const borderRadius = '0.5rem';
  const color = colorMode === 'dark' ? '#e5e2e1' : '#1e293b';
  const focusBorder = '#2A9D8F';
  const invalidBorder = '#ffb4ab';
  const borderColor = colorMode === 'dark' ? '#3d4947' : '#cbd5e1';
  const borderHover = colorMode === 'dark' ? '#879390' : '#94a3b8';
  const backgroundColor = colorMode === 'dark' ? '#1c1b1b' : '#ffffff';

  return useCallback(
    (base, state) => {
      const { isFocused } = state;
      const styles = {
        backgroundColor,
        borderRadius,
        color,
        minHeight,
        paddingLeft: '4px',
        paddingRight: '4px',
        transition: 'all 0.2s',
        borderColor: isError ? invalidBorder : isFocused ? focusBorder : borderColor,
        boxShadow: isError
          ? `0 0 0 1px ${invalidBorder}`
          : isFocused
            ? `0 0 0 1px ${focusBorder}`
            : undefined,
        '&:hover': { borderColor: isFocused ? focusBorder : borderHover },
        '&:hover > div > span': { backgroundColor: borderHover },
        '&:focus': { borderColor: isError ? invalidBorder : focusBorder },
        '&.invalid': { borderColor: invalidBorder, boxShadow: `0 0 0 1px ${invalidBorder}` },
      };
      return mergeWith({}, base, styles);
    },
    [isError, colorMode],
  );
};

export const useMenuStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'menu', Opt, IsMulti> => {
  const { colorMode } = props;
  const { isOpen } = useSelectContext();

  const backgroundColor = colorMode === 'dark' ? '#201f1f' : '#ffffff';
  const borderColor = colorMode === 'dark' ? '#3d4947' : '#cbd5e1';
  const styles = { backgroundColor, zIndex: 1500, border: `1px solid ${borderColor}`, borderRadius: '0.5rem' };

  return useCallback(base => mergeWith({}, base, styles), [isOpen, colorMode]);
};

export const useMenuListStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'menuList', Opt, IsMulti> => {
  const { colorMode } = props;
  const { isOpen } = useSelectContext();

  const borderRadius = '0.5rem';
  const backgroundColor = colorMode === 'dark' ? '#201f1f' : '#ffffff';
  const scrollbarTrack = colorMode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)';
  const scrollbarThumb = colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const scrollbarThumbHover = colorMode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)';
  const styles = {
    borderRadius,
    backgroundColor,
    '&::-webkit-scrollbar': { width: '5px' },
    '&::-webkit-scrollbar-track': { backgroundColor: scrollbarTrack },
    '&::-webkit-scrollbar-thumb': { backgroundColor: scrollbarThumb },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: scrollbarThumbHover },
    '-ms-overflow-style': { display: 'none' },
  };

  return useCallback(base => mergeWith({}, base, styles), [isOpen, colorMode]);
};

export const useOptionStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'option', Opt, IsMulti> => {
  const { colorMode } = props;
  const { isOpen } = useSelectContext();

  const fontSize = '16px';
  const disabled = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.05)';
  const active = '#2A9D8F';
  const focused = colorMode === 'dark' ? 'rgba(42, 157, 143, 0.15)' : 'rgba(42, 157, 143, 0.1)';
  const selected = colorMode === 'dark' ? 'rgba(42, 157, 143, 0.3)' : 'rgba(42, 157, 143, 0.2)';

  return useCallback(
    (base, state) => {
      const { isFocused, isSelected, isDisabled } = state;

      let backgroundColor = 'transparent';
      switch (true) {
        case isDisabled:
          backgroundColor = disabled;
          break;
        case isSelected:
          backgroundColor = selected;
          break;
        case isFocused:
          backgroundColor = focused;
          break;
      }

      const styles = {
        color: colorMode === 'dark' ? '#e5e2e1' : '#1e293b',
        '&:active': { backgroundColor: active, color: '#ffffff' },
        '&:focus': { backgroundColor: active, color: '#ffffff' },
        backgroundColor,
        fontSize,
        cursor: 'pointer',
      };

      return mergeWith({}, base, styles);
    },
    [isOpen, colorMode],
  );
};

export const useIndicatorSeparatorStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'indicatorSeparator', Opt, IsMulti> => {
  const { colorMode } = props;
  const styles = { backgroundColor: colorMode === 'dark' ? '#3d4947' : '#e2e8f0' };

  return useCallback(base => mergeWith({}, base, styles), [colorMode]);
};

export const usePlaceholderStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'placeholder', Opt, IsMulti> => {
  const { colorMode } = props;
  const color = colorMode === 'dark' ? '#bcc9c6' : '#94a3b8';
  const fontSize = '16px';

  return useCallback(base => mergeWith({}, base, { color, fontSize }), [colorMode]);
};

export const useSingleValueStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'singleValue', Opt, IsMulti> => {
  const { colorMode } = props;
  const color = colorMode === 'dark' ? '#e5e2e1' : '#1e293b';
  const fontSize = '16px';
  const styles = { color, fontSize };

  return useCallback(base => mergeWith({}, base, styles), [colorMode]);
};

export const useMultiValueStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'multiValue', Opt, IsMulti> => {
  const { colorMode } = props;

  const backgroundColor = useColorToken('colors', 'primary.500', 'primary.300');
  const color = useOpposingColor(backgroundColor);
  const borderRadius = useToken('radii', 'md');
  const styles = { backgroundColor, color, borderRadius };

  return useCallback(base => mergeWith({}, base, styles), [backgroundColor, colorMode]);
};

export const useMultiValueLabelStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'multiValueLabel', Opt, IsMulti> => {
  const { colorMode } = props;

  const backgroundColor = useColorToken('colors', 'primary.500', 'primary.300');
  const color = useOpposingColor(backgroundColor);
  const styles = { color };

  return useCallback(base => mergeWith({}, base, styles), [colorMode]);
};

export const useMultiValueRemoveStyle = <Opt extends SingleOption, IsMulti extends boolean>(
  props: RSStyleCallbackProps,
): RSStyleFunction<'multiValueRemove', Opt, IsMulti> => {
  const { colorMode } = props;

  const backgroundColor = useColorToken('colors', 'primary.500', 'primary.300');
  const color = useOpposingColor(backgroundColor);
  const styles = {
    color,
    '&:hover': { backgroundColor: 'transparent', color, opacity: 0.8 },
  };

  return useCallback(base => mergeWith({}, base, styles), [colorMode]);
};

export const useRSTheme = (): RSThemeFunction => {
  const borderRadius = useToken('radii', 'md') as unknown as number;

  return useCallback((t: ReactSelect.Theme): ReactSelect.Theme => ({ ...t, borderRadius }), []);
};

export const useMenuPortal = <Opt extends SingleOption, IsMulti extends boolean>(): RSStyleFunction<
  'menuPortal',
  Opt,
  IsMulti
> => {
  const isMobile = useMobile();
  const styles = {
    zIndex: 1500,
  };

  return useCallback(base => mergeWith({}, base, styles), [isMobile]);
};
